import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';

// Base URL for API requests - can be easily changed later
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include JWT token
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Get current auth session
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      // If auth is disabled or token fetch fails, continue without token
      console.log('No auth token available:', error.message);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('Authentication failed - token may be expired');
      // Could trigger a re-login flow here if needed
    }
    return Promise.reject(error);
  }
);

// API service object with methods for different endpoints
const apiService = {
  // Get available files for upload
  getAvailableFiles: async () => {
    try {
      const response = await apiClient.get('/files');
      // Backend returns {files: [...], pagination: {...}, filters_applied: {...}}
      // Frontend expects just the files array
      return response.data.files || [];
    } catch (error) {
      console.error('Error fetching available files:', error);
      // Return empty array if API fails
      return [];
    }
  },

  // Get questions for manual analysis based on selected file
  getQuestions: async (fileId = 'file1') => {
    try {
      const response = await apiClient.get(`/research/manual?file_id=${fileId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching questions for ${fileId}:`, error);
      return [];
    }
  },

  // Get questions for UIv2 manual analysis (Amazon 2024 report)
  getQuestionsV2: async () => {
    try {
      const response = await apiClient.get('/research/manualv2');
      // Ensure we always return an array
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      } else if (data && Array.isArray(data.questions)) {
        return data.questions;
      } else {
        console.warn('API returned non-array data:', data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching v2 questions:', error);
      return [];
    }
  },

  // V2 Batch Research API - Submit multiple questions for Graph Engine processing
  startBatchResearch: async (questions, company = 'Amazon') => {
    try {
      const requestData = {
        questions: questions.map(q => ({
          question_id: q.id || `q_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
          question_text: q.text || q.question || q,
          company: company,
          options: q.options || null
        })),
        enable_progress_tracking: true
      };
      
      console.log('Sending batch research request:', requestData);
      const response = await apiClient.post('/api/v2/research/batch', requestData);
      return response.data;
    } catch (error) {
      console.error('Error starting batch research:', error);
      throw error;
    }
  },

  // Submit manual analysis answers and get AI analysis
  submitManualAnalysis: async (fileId = 'file1', answers, manualAnalysisTime) => {
    try {
      // In a real API, we would post the answers and get back results
      // For json-server, we'll just get the pre-defined analysis for this file
      const response = await apiClient.get(`/analysis_${fileId}`);
      
      // Get the Bedrock response to add citations
      try {
        const bedrockResponse = await fetch(`/mock-data/bedrock-response-${fileId}.json`);
        const bedrockData = await bedrockResponse.json();
        
        // Merge the data
        const analysisData = response.data;
        const enhancedQuestionsData = analysisData.questionsData.map((question) => {
          const bedrockQuestion = bedrockData.answers.find(a => a.id === question.id);
          if (bedrockQuestion) {
            return {
              ...question,
              confidence: bedrockQuestion.confidence,
              page_references: bedrockQuestion.page_references,
              citations: bedrockQuestion.citations
            };
          }
          return question;
        });
        
        return {
          ...analysisData,
          questionsData: enhancedQuestionsData
        };
      } catch (bedrockError) {
        console.error('Error fetching Bedrock data:', bedrockError);
        return response.data;
      }
    } catch (error) {
      console.error(`Error submitting manual analysis for ${fileId}:`, error);
      return {
        aiAnalysisTime: 3.2,
        aiAccuracy: 100,
        questionsData: []
      };
    }
  },

  // Get AI analysis results directly (without manual analysis)
  getAIAnalysis: async (fileId = 'file1') => {
    try {
      const response = await apiClient.get(`/analysis_${fileId}`);
      
      // Get the Bedrock response to add citations
      const bedrockResponse = await fetch(`/mock-data/bedrock-response-${fileId}.json`);
      const bedrockData = await bedrockResponse.json();
      
      // Merge the data
      const analysisData = response.data;
      const enhancedQuestionsData = analysisData.questionsData.map((question, index) => {
        const bedrockQuestion = bedrockData.answers.find(a => a.id === question.id);
        if (bedrockQuestion) {
          return {
            ...question,
            confidence: bedrockQuestion.confidence,
            page_references: bedrockQuestion.page_references,
            citations: bedrockQuestion.citations
          };
        }
        return question;
      });
      
      return {
        ...analysisData,
        questionsData: enhancedQuestionsData
      };
    } catch (error) {
      console.error(`Error fetching AI analysis for ${fileId}:`, error);
      return {
        aiAnalysisTime: 3.2,
        aiAccuracy: 100,
        questionsData: []
      };
    }
  },

  // Get the prompt used for Bedrock
  getBedrockPrompt: async () => {
    try {
      // In a real API, this would fetch the actual prompt used
      // For now, we'll use a static file
      const response = await fetch('/mock-data/bedrock-prompt.json');
      return await response.json();
    } catch (error) {
      console.error('Error fetching Bedrock prompt:', error);
      return { prompt: "Error loading prompt" };
    }
  },

  // Get the Bedrock response for a specific file
  getBedrockResponse: async (fileId = 'file1') => {
    try {
      // In a real API, this would fetch the actual response from Bedrock
      // For now, we'll use static files
      const response = await fetch(`/mock-data/bedrock-response-${fileId}.json`);
      return await response.json();
    } catch (error) {
      console.error(`Error fetching Bedrock response for ${fileId}:`, error);
      return { error: "Error loading Bedrock response" };
    }
  },

  // === GRAPH ENGINE AI RESEARCH METHODS ===

  // Transform manual questions for Graph Engine research (remove correctAnswer)
  transformQuestionsForAI: (manualQuestions) => {
    return manualQuestions.map((q, index) => ({
      question_id: q.id || `q_${Date.now()}_${index}`,
      question_text: q.question,
      company: 'Amazon', // Default company
      priority: 1,
      options: q.options || null
      // Deliberately exclude q.correctAnswer
    }));
  },

  // Start AI research using Graph Engine API
  startAIResearch: async (company, questions, demoMode = true) => {
    try {
      console.log('Starting Graph Engine AI research for company:', company);
      console.log('Questions:', questions);
      
      // Transform questions to Graph Engine format
      const graphEngineQuestions = questions.map((q, index) => ({
        question_id: q.id || q.question_id || `q_${Date.now()}_${index}`,
        question_text: q.text || q.question_text || q.question,
        company: company,
        priority: q.priority || 1,
        options: q.options || null
      }));

      const requestBody = {
        questions: graphEngineQuestions,
        enable_progress_tracking: true
      };

      const response = await apiClient.post('/api/v2/research/batch', requestBody);
      
      console.log('Graph Engine AI research started:', response.data);
      return response.data; // Should contain session_id, success, etc.
      
    } catch (error) {
      console.error('Error starting Graph Engine AI research:', error);
      throw error;
    }
  },

  // Get AI research progress/status (Graph Engine doesn't have separate status endpoint)
  getAIResearchStatus: async (sessionId) => {
    try {
      // Graph Engine returns results immediately, so we simulate status checking
      // In a real implementation, you might want to add a separate status endpoint
      console.log(`Getting AI research status for session ${sessionId}`);
      return {
        session_id: sessionId,
        status: 'processing',
        message: 'Research in progress via Graph Engine'
      };
    } catch (error) {
      console.error(`Error getting AI research status for session ${sessionId}:`, error);
      throw error;
    }
  },

  // Get final AI research results from session endpoint
  getAIResearchResults: async (sessionId) => {
    try {
      console.log(`🔍 Getting AI research results for session ${sessionId}`);
      console.log(`📡 Making request to: /api/v2/research/session/${sessionId}`);
      
      const response = await apiClient.get(`/api/v2/research/session/${sessionId}`);
      
      console.log('✅ Session results retrieved successfully');
      console.log('📊 Response status:', response.status);
      console.log('📋 Response data:', response.data);
      console.log('🔢 Results count:', response.data?.results?.length || 0);
      
      return response.data;
    } catch (error) {
      console.error(`❌ Error getting AI research results for session ${sessionId}:`, error);
      console.error('📊 Error status:', error.response?.status);
      console.error('📋 Error data:', error.response?.data);
      console.error('💬 Error message:', error.message);
      throw error;
    }
  },

  // Complete AI research flow using Graph Engine (no polling needed)
  runAIResearch: async (company, manualQuestions, demoMode = true, onProgress = null) => {
    try {
      // Transform questions for Graph Engine
      const aiQuestions = apiService.transformQuestionsForAI(manualQuestions);
      
      // Start AI research - Graph Engine returns results immediately
      const response = await apiService.startAIResearch(company, aiQuestions, demoMode);
      
      console.log('Graph Engine AI research completed:', response);
      
      // Transform Graph Engine response to match expected format
      const transformedResponse = {
        sessionId: response.session_id,
        status: response.success ? 'completed' : 'failed',
        company: company,
        questionCount: response.total_questions,
        completedQuestions: response.completed_questions,
        duration: response.overall_execution_time,
        results: response.results.map(result => ({
          questionId: result.question_id,
          question: aiQuestions.find(q => q.question_id === result.question_id)?.question_text || '',
          status: result.status,
          answer: result.research_documents?.answer || result.match_answer?.explanation || 'No answer found',
          selectedOption: result.match_answer?.selected_option || null,
          confidence: result.match_answer?.confidence || result.research_documents?.confidence || 0,
          citations: result.research_documents?.citations || [],
          executionTime: result.execution_time,
          timestamp: result.timestamp
        })),
        summary: {
          successRate: response.overall_success_rate,
          totalExecutionTime: response.overall_execution_time,
          averageConfidence: response.results.reduce((acc, r) => 
            acc + (r.match_answer?.confidence || r.research_documents?.confidence || 0), 0
          ) / response.results.length
        }
      };
      
      return transformedResponse;
      
    } catch (error) {
      console.error('Error in Graph Engine AI research flow:', error);
      throw error;
    }
  },

  // === GRAPH ENGINE V2 API METHODS ===

  // Start Graph Engine research (V2 API)
  startGraphEngineResearch: async (questions, company = 'Amazon') => {
    try {
      const requestData = {
        questions: questions.map(q => ({
          question_id: q.id || `q_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
          question_text: q.text || q.question || q,
          company: company,
          options: q.options || null
        })),
        enable_progress_tracking: true
      };
      
      console.log('Starting Graph Engine research:', requestData);
      const response = await apiClient.post('/api/v2/research/batch', requestData);
      return response.data;
    } catch (error) {
      console.error('Error starting Graph Engine research:', error);
      throw error;
    }
  },

  // Get Graph Engine health status
  getGraphEngineHealth: async () => {
    try {
      const response = await apiClient.get('/api/v2/health');
      return response.data;
    } catch (error) {
      console.error('Error getting Graph Engine health:', error);
      throw error;
    }
  },

  // Get Graph Engine metrics
  getGraphEngineMetrics: async () => {
    try {
      const response = await apiClient.get('/api/v2/metrics');
      return response.data;
    } catch (error) {
      console.error('Error getting Graph Engine metrics:', error);
      throw error;
    }
  },

  // === INSIGHTS API METHODS ===

  // Get insights status for a session
  getInsightsStatus: async (sessionId) => {
    try {
      console.log(`🔍 Getting insights status for session ${sessionId}`);
      const response = await apiClient.get(`/api/v2/insights/status/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Error getting insights status for session ${sessionId}:`, error);
      throw error;
    }
  },

  // Get insights results for a session
  getInsightsResults: async (sessionId) => {
    try {
      console.log(`📊 Getting insights results for session ${sessionId}`);
      const response = await apiClient.get(`/api/v2/insights/results/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Error getting insights results for session ${sessionId}:`, error);
      throw error;
    }
  },

  // Get insights orchestrator status
  getInsightsOrchestratorStatus: async () => {
    try {
      const response = await apiClient.get('/api/v2/insights/orchestrator/status');
      return response.data;
    } catch (error) {
      console.error('Error getting insights orchestrator status:', error);
      throw error;
    }
  },

  // Poll for insights completion (automatic generation)
  pollForInsights: async (sessionId, maxAttempts = 60, intervalMs = 3000) => {
    console.log(`🔄 Starting automatic insights polling for session ${sessionId}`);
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const status = await apiService.getInsightsStatus(sessionId);
        
        console.log(`📊 Insights polling attempt ${attempt}/${maxAttempts}: ${status.status}`);
        
        if (status.success && status.status === 'completed') {
          console.log(`✅ Insights completed for session ${sessionId}`);
          const results = await apiService.getInsightsResults(sessionId);
          return { success: true, results, status };
        } else if (status.success && status.status === 'failed') {
          console.log(`❌ Insights failed for session ${sessionId}: ${status.error_message}`);
          return { success: false, error: status.error_message, status };
        } else if (!status.success && status.status === 'not_found') {
          // Insights might not be triggered yet, wait a bit more
          console.log(`⏳ Insights not yet started for session ${sessionId}, waiting...`);
        } else if (status.success && (status.status === 'processing' || status.status === 'pending')) {
          // Still processing
          console.log(`⏳ Insights still processing for session ${sessionId}: ${status.current_stage || 'unknown stage'}`);
        }
        
        // Wait before next attempt
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, intervalMs));
        }
      } catch (error) {
        console.error(`❌ Error during insights polling attempt ${attempt}:`, error);
        
        // If it's a 404, insights might not be started yet
        if (error.response?.status === 404) {
          console.log(`⏳ Insights not found yet (404), continuing to poll...`);
          if (attempt < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, intervalMs));
          }
          continue;
        }
        
        // For other errors, return failure after a few attempts
        if (attempt >= 3) {
          return { success: false, error: error.message };
        }
        
        // Wait and retry for first few attempts
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
    }
    
    console.log(`⏰ Insights polling timeout for session ${sessionId} after ${maxAttempts} attempts`);
    return { success: false, error: 'Insights generation timeout - this may be normal if insights take longer to process' };
  },

  // === ROCK-SOLID BACKEND: SESSION COMPLETION POLLING ===

  // Check session completion status (polling fallback for WebSocket reliability)
  checkSessionCompletionStatus: async (sessionId) => {
    try {
      console.log(`🔍 Checking completion status for session ${sessionId}`);
      const response = await apiClient.get(`/api/v2/research/session/${sessionId}/completion-status`);
      console.log(`📊 Completion status response:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Error checking completion status for session ${sessionId}:`, error);
      throw error;
    }
  },

  // === REPORT GENERATION METHODS ===

  // Generate an analyst report for a session
  generateReport: async (requestData) => {
    try {
      const response = await apiClient.post('/api/v2/reports/generate', requestData);
      return response;
    } catch (error) {
      console.error('Error generating report:', error);
      throw error;
    }
  },

  // Get a specific report by ID
  getReport: async (reportId) => {
    try {
      const response = await apiClient.get(`/api/v2/reports/${reportId}`);
      return response;
    } catch (error) {
      console.error(`Error getting report ${reportId}:`, error);
      throw error;
    }
  },

  // Get all reports for a session
  getSessionReports: async (sessionId) => {
    try {
      const response = await apiClient.get(`/api/v2/reports/session/${sessionId}`);
      return response;
    } catch (error) {
      console.error(`Error getting reports for session ${sessionId}:`, error);
      throw error;
    }
  },

  // === GUARDRAILS SHOWCASE METHODS ===

  getGuardrailsQuestions: async () => {
    try {
      const response = await apiClient.get('/api/v2/guardrails/questions');
      return response.data;
    } catch (error) {
      console.error('Error getting guardrails questions:', error);
      throw error;
    }
  },

  askGuardrailsQuestion: async (questionId) => {
    try {
      const response = await apiClient.post('/api/v2/guardrails/ask', {
        question_id: questionId
      });
      return response.data;
    } catch (error) {
      console.error(`Error asking guardrails question ${questionId}:`, error);
      throw error;
    }
  },

  getGuardrailsHealth: async () => {
    try {
      const response = await apiClient.get('/api/v2/guardrails/health');
      return response.data;
    } catch (error) {
      console.error('Error getting guardrails health:', error);
      throw error;
    }
  },

  // === MARKET DATA METHODS ===

  getMarketChartData: async (ticker = 'AMZN', startDate = '2022-01-01', endDate = '2025-03-31') => {
    try {
      const response = await apiClient.get(
        `/api/v2/market-data/chart/${ticker}?start_date=${startDate}&end_date=${endDate}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching market chart data:', error);
      return null; // Non-critical — chart is optional context
    }
  },

  getMarketSummary: async (ticker = 'AMZN', startDate = '2023-01-01', endDate = '2024-12-31') => {
    try {
      const response = await apiClient.get(
        `/api/v2/market-data/summary/${ticker}?start_date=${startDate}&end_date=${endDate}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching market summary:', error);
      return null;
    }
  }
};

export default apiService;
